import { CSSProperties, ReactNode, ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { classNames } from '@frontend/string';
import { theme } from '@frontend/theme';
import { useTooltip, Text } from '@frontend/design-system';
import { TimelineBarProps, TimelineDataItem, TimelineLegend } from './types';

export const SimpleTimelineBar = ({
  width,
  background = theme.colors.neutral20,
  outlineColor = 'transparent',
  label,
  details,
  onClick,
}: TimelineBarProps) => {
  const { t } = useTranslation('calls');
  const { tooltipProps, triggerProps, Tooltip } = useTooltip({
    trigger: 'hover',
    theme: 'light',
    showArrow: false,
  });

  return (
    <div
      className='timeline-bar'
      css={{
        position: 'relative',
        width,
        height: 32,
      }}
    >
      <div
        {...triggerProps}
        className={classNames('timeline-bar-shape', onClick && 'clickable')}
        onClick={() => onClick?.({ label, details })}
        css={[
          {
            height: 32,
            background: background,
            borderRadius: theme.borderRadius.medium,
          },
          onClick && {
            outline: `2px solid ${outlineColor}`,
            outlineOffset: -2,
            cursor: 'pointer',
          },
        ]}
      ></div>
      <Tooltip
        {...tooltipProps}
        css={{
          borderRadius: theme.borderRadius.large,
          width: 230,
          padding: 0,
        }}
      >
        <div
          css={{
            display: 'grid',
            gap: theme.spacing(1),
            padding: theme.spacing(3),
            fontWeight: 'normal',
          }}
        >
          <Text as='span' size='small'>
            {label}
          </Text>
          {details?.map((detail) => (
            <TooltipDetailRow key={detail.title} title={detail.title} label={detail.label} />
          ))}
          {onClick && (
            <>
              <div css={{ borderTop: `1px solid ${theme.colors.neutral10}` }} />
              <Text
                as='span'
                size='small'
                css={{
                  fontStyle: 'italic',
                }}
              >
                {t('Click the bar to learn more')}
              </Text>
            </>
          )}
        </div>
      </Tooltip>
    </div>
  );
};

export const DotWithLabel = ({
  label,
  color,
  textColor = 'default',
}: {
  label: string;
  color: CSSProperties['color'];
  textColor?: 'default' | 'light';
}) => {
  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: 'max-content max-content',
        gap: theme.spacing(1),
        alignItems: 'center',
      }}
    >
      <div css={{ borderRadius: '50%', background: color, width: 8, height: 8 }} />
      <Text as='span' size='medium' weight='bold' color={textColor}>
        {label}
      </Text>
    </div>
  );
};

export const TooltipDetailRow = ({ title, label }: { title: ReactNode; label: ReactNode }) => {
  return (
    <>
      <div css={{ borderTop: `1px solid ${theme.colors.neutral10}` }} />

      <div
        css={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: theme.spacing(1),
        }}
      >
        {typeof title === 'string' ? (
          <Text as='span' size='medium'>
            {title}
          </Text>
        ) : (
          title
        )}
        {typeof label === 'string' ? (
          <Text as='span' size='medium' weight='bold'>
            {label}
          </Text>
        ) : (
          label
        )}
      </div>
    </>
  );
};

export const SimpleTimelineLegend = ({ legends }: { legends: TimelineLegend[] }) => {
  return (
    <div css={{ display: 'grid', gap: theme.spacing(2), justifyContent: 'center', gridAutoFlow: 'column' }}>
      {legends.map((legend) => (
        <DotWithLabel key={legend.label} label={legend.label} color={legend.color} textColor='light' />
      ))}
    </div>
  );
};

export const SimpleTimelineAxis = ({ startLabel, endLabel }: { startLabel: string; endLabel: string }) => {
  return (
    <div css={{ display: 'grid', justifyContent: 'space-between', gridAutoFlow: 'column' }}>
      <Text size='medium' color='light'>
        {startLabel}
      </Text>
      <Text size='medium' color='light'>
        {endLabel}
      </Text>
    </div>
  );
};

export const SimpleTimelineChart = <T extends Record<string, ComponentType<TimelineBarProps>>>({
  barTypes,
  data,
  legends,
  axis,
  options = {
    showLegends: true,
    showAxis: true,
  },
}: {
  barTypes?: T;
  data: TimelineDataItem<T>[];
  legends?: TimelineLegend[];
  axis?: {
    startLabel: string;
    endLabel: string;
  };
  options?: {
    showLegends?: boolean;
    showAxis?: boolean;
  };
}) => {
  const shouldShowLegends = options.showLegends && legends && legends.length > 0;
  const shouldShowAxis = options.showAxis && axis && axis.startLabel && axis.endLabel;
  return (
    <div css={{ display: 'grid', gap: theme.spacing(2) }}>
      <div
        css={{
          display: 'flex',
          position: 'relative',

          '&:has(.timeline-bar-shape.clickable:hover) :not(.timeline-bar-shape.clickable:hover).timeline-bar-shape': {
            opacity: 0.5,
          },
        }}
      >
        {data.map((item, index) => {
          const Bar = (barTypes?.[item.type] ?? SimpleTimelineBar) as ComponentType<TimelineBarProps>;
          return (
            <Bar
              key={index}
              label={item.label}
              width={item.width}
              details={item.details}
              onClick={item.onClick}
              background={item.background}
              outlineColor={item.outlineColor}
            />
          );
        })}
      </div>

      {shouldShowAxis && <SimpleTimelineAxis startLabel={axis.startLabel} endLabel={axis.endLabel} />}
      {shouldShowLegends && <SimpleTimelineLegend legends={legends} />}
    </div>
  );
};
